import { X, MoveLeft } from "lucide-react";
import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTeam } from "contexts/TeamContext";
import type { Team, TeamMember } from "../types";
import { TeamView } from "../TeamView/TeamView";
import { TeamEdit } from "../TeamEdit/TeamEdit";
import { TeamService } from "../../../services/TeamService";

type TeamDetailsState = {
  team?: Team | Team[];
};

const resolveTeamFromState = (state: TeamDetailsState): Team | null => {
  if (!state?.team) return null;
  if (Array.isArray(state.team)) {
    return state.team[0] ?? null;
  }
  return state.team;
};

export const TeamDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setTeams, teamMembers, setTeamMembers } = useTeam();
  const teamMembersEditBaselineRef = useRef<TeamMember[] | null>(null);

  const initialTeam = resolveTeamFromState((state as TeamDetailsState) ?? {});
  const [team, setTeam] = useState<Team | null>(initialTeam);
  const teamNotifications = team?.notifications;
  const teamCauseTypeIds = team?.causeTypeIds;
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(team?.name ?? "");
  const [notifications, setNotifications] = useState<string[]>(() =>
    Array.isArray(teamNotifications)
      ? teamNotifications.filter(
          (day): day is string => typeof day === "string" && day.length > 0
        )
      : []
  );
  const [causeTypeIds, setCauseTypeIds] = useState<string[]>(() =>
    Array.isArray(teamCauseTypeIds)
      ? teamCauseTypeIds.filter(
          (causeId): causeId is string =>
            typeof causeId === "string" && causeId.length > 0
        )
      : []
  );
  const [reportDay, setReportDay] = useState(
    typeof team?.reportDay === "string" ? team.reportDay : ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEdit = () => {
    if (!team) return;
    setError(null);
    setName(team.name ?? "");
    setNotifications(
      Array.isArray(team.notifications) ? team.notifications : []
    );
    setCauseTypeIds(Array.isArray(team.causeTypeIds) ? team.causeTypeIds : []);
    setReportDay(typeof team.reportDay === "string" ? team.reportDay : "");
    teamMembersEditBaselineRef.current = (
      Array.isArray(teamMembers) ? teamMembers : []
    ).map((m: TeamMember) => ({ ...m }));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (!team) return;
    const baseline = teamMembersEditBaselineRef.current;
    if (baseline) {
      setTeamMembers(baseline.map((m) => ({ ...m })));
    }
    teamMembersEditBaselineRef.current = null;
    setName(team.name ?? "");
    setNotifications(
      Array.isArray(team.notifications) ? team.notifications : []
    );
    setCauseTypeIds(Array.isArray(team.causeTypeIds) ? team.causeTypeIds : []);
    setReportDay(typeof team.reportDay === "string" ? team.reportDay : "");
    setError(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!team?.id) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("TeamsOverview name is required");
      return;
    }

    setSubmitting(true);
    setError(null);

    const baseline = teamMembersEditBaselineRef.current ?? [];

    try {
      await TeamService.updateTeam(
        team.id,
        trimmed,
        notifications,
        causeTypeIds,
        reportDay
      );
      await TeamService.applyTeamMemberRemovalsSinceBaseline(
        team.id,
        baseline,
        teamMembers
      );

      setTeams((prev: unknown) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((row: Team) =>
          row?.id === team.id
            ? { ...row, name: trimmed, notifications, causeTypeIds, reportDay }
            : row
        );
      });
      setTeam((prev) =>
        prev
          ? { ...prev, name: trimmed, notifications, causeTypeIds, reportDay }
          : prev
      );
      teamMembersEditBaselineRef.current = null;
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update team");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="root">
      <div className="settings-element-header">
        {!isEditing && (
          <MoveLeft
            onClick={() => navigate("/app/settings/teams")}
            style={{ marginLeft: "5px", marginRight: "5px" }}
          />
        )}
        {isEditing && (
          <X
            onClick={() => handleCancelEdit()}
            style={{ marginLeft: "5px", marginRight: "5px" }}
          />
        )}
        <p className={"settings-element-title page-title"}>{team?.name}</p>
        <div />
        <button
          type="button"
          onClick={isEditing ? handleSave : handleEdit}
          disabled={!team || submitting}
          style={{
            marginLeft: "5px",
            marginRight: "5px",
            background: "none",
            border: "none",
            fontWeight: 600,
            cursor: "pointer",
            fontSize: "18px",
          }}
        >
          {isEditing ? (submitting ? "Saving..." : "Save") : "Edit"}
        </button>
      </div>

      {!isEditing && team && (
        <TeamView
          selectedTeam={team}
          organizationId={team.organizationId}
          selectedNotifications={notifications}
          onNotificationsChange={setNotifications}
          reportDay={reportDay}
        />
      )}
      {isEditing && team && (
        <TeamEdit
          teamId={team.id}
          name={name}
          setName={setName}
          organizationId={team.organizationId}
          selectedNotifications={notifications}
          onNotificationsChange={setNotifications}
          selectedCauseTypeIds={causeTypeIds}
          onCauseTypeIdsChange={setCauseTypeIds}
          reportDay={reportDay}
          onReportDayChange={setReportDay}
        />
      )}
      {error ? <p style={{ color: "#c0392b" }}>{error}</p> : null}
    </div>
  );
};
